import React, { forwardRef, useMemo, useCallback, useState, useEffect } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import { AnimatePresence } from "framer-motion";
import Fade from "../animations/Fade";
import Tooltip from "../tooltip/Tooltip";
import BaseMapHeader from "../map/BaseMapHeader";
import MapControls from "../map/MapControls";
import BlueMarkerIcon from "../map/BlueMarkerIcon";
import { getIsExtraSmall } from "../../hooks";
import loadable from "@loadable/component";
import { inPdfMode } from "../../constants";

const LoadableMapboxGLComponent = loadable(async () => {
    const { default: ReactMapboxGl } = await import(/* webpackChunkName: "ReactMapboxGl" */ "react-mapbox-gl");
    const MapboxGLComponent = ReactMapboxGl({
        preserveDrawingBuffer: inPdfMode,
        accessToken: "pk.eyJ1IjoidG5iM2siLCJhIjoiY2ptNm14bHQ2MWNubzN3bjJmZjJxOWlwOCJ9.JM2XrfZHVM26uw2bQAjZsA",
        keyboard: false,
        logoPosition: getIsExtraSmall() ? "bottom-right" : "bottom-left",
    });
    return MapboxGLComponent;
});
const LoadableMarker = loadable(async () => {
    const { Marker } = await import(/* webpackChunkName: "ReactMapboxGl" */ "react-mapbox-gl");
    return Marker;
});

let timeoutId;
export const useMapOverlay = () => {
    const [isMapOverlayVisible, setIsMapOverlayVisible] = useState(false);
    const [mapOverlayText, setMapOverlayText] = useState("");
    const showMapOverlay = useCallback(text => {
        setIsMapOverlayVisible(true);
        clearTimeout(timeoutId);
        setMapOverlayText(text);
        timeoutId = setTimeout(() => {
            setIsMapOverlayVisible(false);
        }, 1500);
    }, []);
    return {
        showMapOverlay,
        mapOverlayText,
        isMapOverlayVisible,
    };
};
const flyToOptions = {
    padding: {
        bottom: 108,
    },
};

const Map = forwardRef(function Map(props, ref) {
    const {
        style,
        markerClasses,
        inItinerary,
        isMapOverlayVisible,
        mapOverlayText,
        className,
        viewport,
        reCenter,
        markers,
        activeMarkerIds,
        zoomIn,
        zoomOut,
        children,
        containerStyle,
        activeSecondaryMarkerIds,
        isAutoFollowEnabled,
        setIsAutoFollowEnabled,
        showAutoFollow,
        isSecondaryLayout,
        isTertiaryLayout,
        isQuaternaryLayout,
        mapClassName,
        onMarkerClick,
        onMarkerMouseEnter,
        onMarkerMouseLeave,
        onMapboxViewportChanges,
        onManualInteraction,
        withMapControls,
        showReCenter,
        useTwoColumnLayout,
        wrapperMapRef,
        mapboxInstance,
        onSearchAreaClick,
    } = props;
    const [hasMapStyleLoad, setHasMapStyleLoad] = useState(false);
    const center = useMemo(() => [viewport.longitude, viewport.latitude], [viewport.longitude, viewport.latitude]);
    const zoom = useMemo(() => [viewport.zoom], [viewport.zoom]);
    const toggleIsAutoFollowActive = useCallback(() => setIsAutoFollowEnabled(!isAutoFollowEnabled), [
        isAutoFollowEnabled,
        setIsAutoFollowEnabled,
    ]);
    const _flyToOptions = withMapControls && useTwoColumnLayout ? flyToOptions : null;

    useEffect(() => {
        if (!mapboxInstance) {
            return;
        }
        mapboxInstance.on("wheel", onManualInteraction);
        return () => {
            mapboxInstance.off("wheel", onManualInteraction);
        };
        // eslint-disable-next-line
    }, [mapboxInstance]);

    return (
        <div ref={wrapperMapRef} className={className} style={{ ...style, backgroundColor: "#efe9e1" }}>
            {withMapControls && (
                <MapControls
                    showReCenter={showReCenter}
                    reCenter={reCenter}
                    zoomIn={zoomIn}
                    zoomOut={zoomOut}
                    showAutoFollow={showAutoFollow}
                    isAutoFollowActive={isAutoFollowEnabled}
                    toggleIsAutoFollowActive={toggleIsAutoFollowActive}
                    useTwoColumnLayout={useTwoColumnLayout}
                    onSearchAreaClick={onSearchAreaClick}
                />
            )}
            {!withMapControls && (
                <BaseMapHeader
                    inItinerary={inItinerary}
                    showReCenter={!!reCenter}
                    reCenter={reCenter}
                    zoomIn={zoomIn}
                    zoomOut={zoomOut}
                    showAutoFollow={showAutoFollow}
                    isAutoFollowActive={isAutoFollowEnabled}
                    toggleIsAutoFollowActive={toggleIsAutoFollowActive}
                    isSecondaryLayout={isSecondaryLayout}
                    isTertiaryLayout={isTertiaryLayout}
                    isQuaternaryLayout={isQuaternaryLayout}
                />
            )}
            <LoadableMapboxGLComponent
                fallback={<div style={containerStyle} className={`js__mapbox-gl ${mapClassName}`}></div>}
                ref={ref}
                className={`js__mapbox-gl ${mapClassName} ${hasMapStyleLoad ? "has-loaded" : ""}`}
                zoom={zoom}
                center={center}
                containerStyle={containerStyle}
                // eslint-disable-next-line
                style="mapbox://styles/ocreurer/ckmlzz4pu3a5r17po0laaadgk"
                //Update viewport state
                onZoomEnd={onMapboxViewportChanges}
                onMoveEnd={onMapboxViewportChanges}
                onPitchEnd={onMapboxViewportChanges}
                onDblClick={onManualInteraction}
                onDragEnd={onManualInteraction}
                onTouchEnd={onManualInteraction}
                flyToOptions={_flyToOptions}
                onStyleLoad={() => setHasMapStyleLoad(true)}
            >
                <AnimatePresence initial={false}>
                    {isMapOverlayVisible && (
                        <Fade
                            className="trpr__map-overlay"
                            transition={{
                                duration: 0.15,
                                ease: "easeOut",
                            }}
                        >
                            <svg
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    fillRule="evenodd"
                                    clipRule="evenodd"
                                    d="M20.3582 3.58402C20.3453 3.03188 19.8873 2.59476 19.3351 2.60768C18.783 2.6206 18.3459 3.07866 18.3588 3.6308L18.399 5.34907C16.8785 4.06638 14.9603 3.22265 12.8279 3.03609C7.32607 2.55474 2.47575 6.62464 1.9944 12.1265C1.51305 17.6283 5.58296 22.4786 11.0848 22.96C16.5866 23.4413 21.4369 19.3714 21.9183 13.8696C21.9664 13.3194 21.5594 12.8344 21.0093 12.7862C20.4591 12.7381 19.974 13.1451 19.9259 13.6953C19.5408 18.0967 15.6606 21.3527 11.2591 20.9676C6.85764 20.5825 3.60171 16.7023 3.98679 12.3008C4.37187 7.89932 8.25213 4.6434 12.6536 5.02848C14.4121 5.18232 15.9869 5.89318 17.2207 6.97331L15.2236 7.2909C14.6782 7.37765 14.3064 7.89012 14.3931 8.43555C14.4799 8.98098 14.9923 9.35282 15.5378 9.26608L19.6097 8.61851C20.104 8.5399 20.4641 8.10792 20.4524 7.60753L20.3582 3.58402Z"
                                    fill="white"
                                />
                            </svg>
                            <span className="trpr__map-overlay-title">{mapOverlayText}</span>
                        </Fade>
                    )}
                </AnimatePresence>
                {children}
                {useMemo(() => {
                    return [...markers]
                        .reverse()
                        .map(({ longitude, latitude, id, label, showTooltip, tooltipLabel, type }, index) => {
                            const coordinates = [longitude, latitude];
                            if (type === "BLUE_MARKER") {
                                return (
                                    <LoadableMarker
                                        key={id}
                                        coordinates={coordinates}
                                        onClick={() => onMarkerClick(id)}
                                        onMouseEnter={() => onMarkerMouseEnter(id)}
                                        onMouseLeave={() => onMarkerMouseLeave(id)}
                                        style={{ zIndex: 3 }}
                                    >
                                        <span data-id={id}>
                                            <BlueMarkerIcon />
                                        </span>
                                    </LoadableMarker>
                                );
                            }
                            const inView = activeMarkerIds.includes(id);
                            const inPlaceView = activeSecondaryMarkerIds.includes(id);
                            const markerClassNames = classNames("js__map-marker", "trpr__marker--stop", markerClasses, {
                                "trpr__marker--stop--inView": inView,
                                "trpr__marker--stop--inPlaceView": inPlaceView,
                                "trpr__marker--stop--autofollow-disabled": !isAutoFollowEnabled,
                            });
                            return (
                                <LoadableMarker
                                    key={id}
                                    coordinates={coordinates}
                                    onClick={() => onMarkerClick(id)}
                                    onMouseEnter={() => onMarkerMouseEnter(id)}
                                    onMouseLeave={() => onMarkerMouseLeave(id)}
                                    style={{ zIndex: inView && inPlaceView ? 5 : inView || inPlaceView ? 4 : 3 }}
                                >
                                    <span data-id={id} className={markerClassNames}>
                                        {label}
                                    </span>
                                    {showTooltip && <Tooltip className="is-floating">{tooltipLabel}</Tooltip>}
                                </LoadableMarker>
                            );
                        });
                }, [
                    isAutoFollowEnabled,
                    markerClasses,
                    markers,
                    activeMarkerIds,
                    activeSecondaryMarkerIds,
                    onMarkerClick,
                    onMarkerMouseEnter,
                    onMarkerMouseLeave,
                ])}
            </LoadableMapboxGLComponent>
        </div>
    );
});

Map.defaultProps = {
    activeMarkerIds: [],
    activeSecondaryMarkerIds: [],
    onMarkerClick: () => {},
    onMarkerMouseEnter: () => {},
    onMarkerMouseLeave: () => {},
    onManualInteraction: () => {},
    isMapOverlayVisible: false,
    mapOverlayText: "Changing...",
};

Map.propTypes = {
    onMapboxEnd: PropTypes.func,
    reCenter: PropTypes.func,
    activeMarkerIds: PropTypes.arrayOf(PropTypes.string),
    zoomIn: PropTypes.func,
    zoomOut: PropTypes.func,
    containerStyle: PropTypes.object,
    viewport: PropTypes.shape({
        longitude: PropTypes.number.isRequired,
        latitude: PropTypes.number.isRequired,
        zoom: PropTypes.number,
    }).isRequired,
    markers: PropTypes.arrayOf(
        PropTypes.shape({
            longitude: PropTypes.number.isRequired,
            latitude: PropTypes.number.isRequired,
        }),
    ),
};

export default Map;
